<script setup>
import { ref, watch } from "vue";


// Define props correctly
const props = defineProps({
  type: { type: String, default: "default" },
  size: { type: String, default: "medium" },
  rounded: { type: Boolean, default: true },
  outlined: { type: Boolean, default: true },
  shadow: { type: Boolean, default: false },
  icon: String, // Normal icon
  hoverIcon: String, // Hover icon
  loading: Boolean,
  disabled: Boolean,
});

// Define emits (correct for <script setup>)
const emit = defineEmits(["click"]);

// Reactive icon state
const iconSrc = ref(props.icon || "");

// Watch for prop changes
watch(() => props.icon, (newIcon) => {
  iconSrc.value = newIcon;
});

// Hover handlers
const handleMouseEnter = () => {
  if (props.hoverIcon) {
    iconSrc.value = props.hoverIcon;
  }
};

const handleMouseLeave = () => {
  if (props.icon) {
    iconSrc.value = props.icon;
  }
};
</script>
<template>
  <button
      :class="[
      'btn',
      `btn-${type}`,
      `btn-${size}`,
      { 'is-loading': loading },
      { 'has-icon': icon },
      { 'is-rounded': rounded },
      { 'is-outlined': outlined },
      { 'with-shadow': shadow }
    ]"
      :disabled="disabled"
      @click="disabled ? null : $emit('click')"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
  >
    <!-- Dynamic Icon -->
    <img v-if="icon" class="btn-icon transition-all" :src="iconSrc" alt="icon" />

    <span v-if="loading" class="btn-loading-spinner"></span>
    <slot></slot>
  </button>
</template>

<style scoped>
.btn {
    /* basic button styles */
    padding: 0.5em 1em;
    border: none;
    cursor: pointer;
    transition: background-color 0.3s, box-shadow 0.3s, color 0.3s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5em;
    position: relative;
    text-align: center;
    font-weight: 600;
}

/* Size variations */
.btn-small {
    font-size: 0.8rem;
    padding: 0.25em 0.5em;
}

.btn-medium {
    font-size: 1rem;
    padding: 0.5em 1em;
}

.btn-md-large {
  font-size: 1.2rem;
  padding: 0.75rem 1.5rem;
}

.btn-large {
    font-size: 1.2rem;
    padding: 0.75em 1.5em;
}

.btn-outlinesmall {
    font-size: 14px;
    font-weight: 600;
}

/* Type (color) variations */
.btn-primary {
    background-color: #007bff;
    color: white;
}

.btn-primary:hover {
    background-color: black;
    color: white;
}

.btn-secondary {
    background-color: #6c757d;
    color: white;
}

.btn-secondary:hover {
    background-color: black;
    color: white;
}

.btn-black {
    background: #000;
    color: white;
}

.btn-black:hover {
    background: #6c757d;
    color: white;
}

.btn-outline-black {
    border: 1px solid #000;
    color: #000;
    background: white;
}

.btn-outline-black:hover {
    background: black;
    color: white;
}

.btn-default {
    background-color: #f8f9fa;
    color: black;
}

/* Additional styles */
.btn-rounded {
    border-radius: 50px;
}

.btn-outlined {
    background: none;
    border: 1px solid;
}

.btn-outlined:hover {
    background-color: black;
    color: white;
}

.btn-with-shadow {
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.btn:disabled {
    cursor: not-allowed;
    opacity: 0.65;
}

.btn-icon {
  transition: filter 0.3s ease-in-out, transform 0.3s ease-in-out;
}

/* Loading spinner styles */
.btn-loading-spinner {
    border: 2px solid #f3f3f3;
    border-top: 2px solid #3498db;
    border-radius: 50%;
    width: 14px;
    height: 14px;
    animation: spin 2s linear infinite;
}

.is-rounded {
    @apply rounded-15xl;
}

.is-outlined {
    background: none;
    border: 1px solid;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}
</style>
